import { Controller } from "@hotwired/stimulus";

// Shows the fields the selected rule type and
// adjustment type call for, and previews what the
// adjustment does to a price while the merchant types.
//
// `PricingRule#apply` ADDS the adjustment: a POSITIVE
// value RAISES the price and a negative one discounts
// it. Nothing else on the form says so, and after
// saving nothing anywhere shows which way a rule goes.
//
// Everything the preview needs (copy, currency,
// reference price) rides on the preview element's own
// data attributes, written by
// `Admin::Menus::PricingRules::FormFieldsComponent`.
// This controller mounts on the <form>, which four
// different views render; the component is the only
// thing that knows the item, and its spec pins these
// attributes so a rename there reddens Ruby rather
// than silently killing the preview.
export default class extends Controller {
  static targets = [
    "ruleTypeDependent",
    "percentageField",
    "fixedAmountField",
    "percentageInput",
    "fixedAmountInput",
    "preview",
    "previewDirection",
    "previewAmounts",
    "previewHint",
  ];

  connect() {
    this.updateRuleTypeFields();
    this.updateAdjustmentFields();
  }

  ruleTypeChanged() {
    this.updateRuleTypeFields();
  }

  adjustmentTypeChanged() {
    this.updateAdjustmentFields();
  }

  adjustmentChanged() {
    this.updatePreview();
  }

  updateRuleTypeFields() {
    const select = this.element.querySelector(
      "[data-pricing-rule-form-rule-type]"
    );
    if (!select) return;

    const selectedType = select.value;

    this.ruleTypeDependentTargets.forEach(
      (el) => {
        const showWhen = el.dataset.showWhen;
        if (!showWhen) return;

        const types = showWhen
          .split(",")
          .map((v) => v.trim());
        const shouldShow =
          types.includes(selectedType);
        el.classList.toggle("hidden", !shouldShow);

        el.querySelectorAll(
          "input, select"
        ).forEach((input) => {
          input.disabled = !shouldShow;
        });
      }
    );
  }

  updateAdjustmentFields() {
    const select = this.adjustmentTypeSelect();
    if (!select) return;

    const isPercentage =
      select.value === "percentage";

    if (this.hasPercentageFieldTarget) {
      this.percentageFieldTarget.classList.toggle(
        "hidden",
        !isPercentage
      );
      this.percentageFieldTarget
        .querySelectorAll("input")
        .forEach((input) => {
          input.disabled = !isPercentage;
        });
    }

    if (this.hasFixedAmountFieldTarget) {
      this.fixedAmountFieldTarget.classList.toggle(
        "hidden",
        isPercentage
      );
      this.fixedAmountFieldTarget
        .querySelectorAll("input")
        .forEach((input) => {
          input.disabled = isPercentage;
        });
    }

    this.updatePreview();
  }

  adjustmentTypeSelect() {
    return this.element.querySelector(
      "[data-pricing-rule-form-adjustment-type]"
    );
  }

  updatePreview() {
    if (!this.hasPreviewTarget) return;

    const preview = this.previewTarget;
    const result = this.previewResult();

    // Zero, blank or unparseable says nothing worth
    // saying: show nothing rather than a "$140 to
    // $140" that reads as a bug.
    if (!result) {
      preview.classList.add("hidden");
      return;
    }

    const data = preview.dataset;
    const raises = result.raises;

    preview.classList.remove("hidden");
    preview.classList.toggle("bg-warning/10", raises);
    preview.classList.toggle("bg-base-200", !raises);

    this.previewDirectionTarget.textContent = raises
      ? data.raisesText
      : data.lowersText;
    this.previewDirectionTarget.classList.toggle(
      "text-warning",
      raises
    );
    this.previewDirectionTarget.classList.toggle(
      "text-base-content/70",
      !raises
    );

    this.previewAmountsTarget.textContent = fill(
      data.amountsText,
      {
        name: data.referenceLabel || "",
        // The server already printed the reference
        // price; only the result is composed here.
        from: data.referenceDisplay,
        to: this.money(result.to),
      }
    );

    this.previewHintTarget.classList.toggle(
      "hidden",
      !raises
    );
    if (raises) {
      this.previewHintTarget.textContent = fill(
        data.hintText,
        { value: String(-result.typed) }
      );
    }
  }

  // Mirrors the server exactly: `PricingRule#apply`
  // adds the adjustment and never returns a negative
  // price, then `Pricing::Engine#drop_cents` floors a
  // rule-adjusted price to whole currency units. A
  // preview showing cents the engine will drop is a
  // preview that lies. Sub-unit aware, so zero-decimal
  // currencies are a no-op, same as drop_cents.
  previewResult() {
    const select = this.adjustmentTypeSelect();
    if (!select) return null;

    const data = this.previewTarget.dataset;
    const subunit = parseInt(data.subunitToUnit, 10);
    const amount = parseFloat(data.referenceAmount);

    // Without the server's format there is no honest
    // way to print a price, so print nothing.
    if (!data.amountTemplate) return null;
    if (!Number.isFinite(subunit) || subunit < 1) {
      return null;
    }
    if (!Number.isFinite(amount)) return null;

    // The adjustment select carries no blank option,
    // so whatever it reads is the type that would be
    // submitted right now.
    const percentage = select.value === "percentage";
    const typed = this.typedAdjustment(percentage);
    if (typed === null || typed === 0) return null;

    const baseMinor = Math.round(amount * subunit);
    const deltaMinor = roundHalfAwayFromZero(
      percentage
        ? baseMinor * (typed / 100)
        : typed * subunit
    );

    const adjusted = Math.max(
      0,
      baseMinor + deltaMinor
    );
    const floored =
      Math.floor(adjusted / subunit) * subunit;

    return {
      // The direction the merchant is being taught is
      // the SIGN THEY TYPED, not the rounded outcome:
      // a +0.1% that floors back to the same price
      // still means "positive raises".
      raises: typed > 0,
      typed,
      from: amount,
      to: floored / subunit,
    };
  }

  typedAdjustment(percentage) {
    const input = percentage
      ? this.percentageInput()
      : this.fixedAmountInput();
    if (!input) return null;

    const value = parseFloat(input.value);

    return Number.isFinite(value) ? value : null;
  }

  percentageInput() {
    return this.hasPercentageInputTarget
      ? this.percentageInputTarget
      : null;
  }

  fixedAmountInput() {
    return this.hasFixedAmountInputTarget
      ? this.fixedAmountInputTarget
      : null;
  }

  // Printed the way the SERVER prints a price, not the
  // way this browser would. The template comes from
  // `Money#format`, so the symbol, the ISO code and
  // their arrangement are the server's; only the digits
  // are grouped here, and `drop_cents` guarantees a
  // whole number of units, so there is never a decimal
  // to render.
  //
  // This used to be `Intl.NumberFormat(document
  // .documentElement.lang || undefined, ...)`. That
  // attribute is empty on admin pages, so Intl fell
  // back to the VIEWER'S browser locale: the preview
  // read "MX$114.00" where the same item renders
  // "$114 MXN" everywhere else, and two merchants on
  // one page could see two different formats.
  money(units) {
    const data = this.previewTarget.dataset;

    return fill(data.amountTemplate, {
      amount: groupDigits(units, data.thousandsSeparator),
    });
  }
}

// The gettext templates arrive with their placeholders
// intact so translators keep the word order.
function fill(template, values) {
  if (!template) return "";

  return Object.entries(values).reduce(
    (text, [key, value]) =>
      text.replaceAll(`%{${key}}`, value),
    template
  );
}

// Groups an integer with the separator the SERVER's
// formatter used. An empty separator is a real answer
// for a locale that groups with nothing.
function groupDigits(units, separator) {
  const sign = units < 0 ? "-" : "";
  const digits = String(Math.abs(Math.round(units)));
  if (!separator) return sign + digits;

  return (
    sign +
    digits.replace(/\B(?=(\d{3})+(?!\d))/g, separator)
  );
}

// Money's rounding_mode is ROUND_HALF_UP, which rounds
// away from zero. JS `Math.round` breaks ties towards
// +Infinity, which disagrees on negative halves.
function roundHalfAwayFromZero(value) {
  return value < 0
    ? -Math.round(-value)
    : Math.round(value);
}
